import { experience, type RequestErrorBody } from '@logto/schemas';
import { useCallback, useMemo } from 'react';
import { validate } from 'superstruct';

import { accountSetupIncompleteErrorDataGuard } from '@/types/guard';

import { usePromiseConfirmModal } from './use-confirm-modal';
import { type ErrorHandlers } from './use-error-handler';
import useNavigateToAccountSetup from './use-navigate-to-account-setup';
import useNavigateWithPreservedSearchParams from './use-navigate-with-preserved-search-params';

/** Handles a social sign-in refused because the related account has no password yet. */
const useAccountSetupIncompleteErrorHandler = (): ErrorHandlers => {
  const navigate = useNavigateWithPreservedSearchParams();
  const navigateToAccountSetup = useNavigateToAccountSetup();
  const { show } = usePromiseConfirmModal();

  const errorCallback = useCallback(
    async (error: RequestErrorBody) => {
      const [, data] = validate(error.data, accountSetupIncompleteErrorDataGuard);
      const [confirmed] = await show({
        ModalContent: error.message,
        confirmText: 'description.set_password',
      });

      if (confirmed && data) {
        navigateToAccountSetup(data.email, { replace: true });
        return;
      }

      navigate('/' + experience.routes.signIn, { replace: true });
    },
    [navigate, navigateToAccountSetup, show]
  );

  return useMemo<ErrorHandlers>(
    () => ({ 'user.account_setup_incomplete': errorCallback }),
    [errorCallback]
  );
};

export default useAccountSetupIncompleteErrorHandler;
